import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * A function that keeps one identity for the component's life and calls the
 * callback of the last render. A memoized child that takes it renders again
 * only when its other props change, and a call, such as a click, never sees
 * the values of an older render.
 *
 * Call it from event handlers, not during render: it holds the callback of
 * the last commit.
 *
 * useCallback cannot do this: with the values as dependencies it changes
 * identity whenever they do, and with none it keeps the first render's
 * values. React proposed this hook as useEvent
 * (https://github.com/reactjs/rfcs/pull/220) but did not ship it. Its
 * successor, useEffectEvent in React 19.2, is for effects only and must not
 * be passed to other components, so a handler passed as a prop still needs
 * this one.
 */
const useStableCallback = <Args extends unknown[], Result>(
    callback: (...args: Args) => Result,
) => {
    const callbackRef = useRef(callback);
    useLayoutEffect(() => {
        callbackRef.current = callback;
    });
    return useCallback(
        (...args: Args): Result => callbackRef.current(...args),
        [],
    );
};

export default useStableCallback;
